import { useRouter } from "next/router";
import { useEffect } from "react";

import { BRAND_PATHS, FEATURE_FLAGS } from "@chef/constants";
import { AppShell } from "@chef/feature-app-shell";
import { useFlag } from "@chef/feature-flags";
import { useExtendedBasketData } from "@chef/state-management/hooks";
import { useIntercom } from "@chef/hooks";
import { isPickAndMixProduct } from "@chef/helpers";
import { Button, Card } from "@chef/components";
import { PageLoader } from "@chef/smart-components";
import { isEmptyArray, isNotEmptyArray } from "@chef/utils/array";

import type { WeekYear } from "@chef/types/common";

import { Summary } from "../lib/Summary";
import { CheckoutExtrasGrid } from "../lib/CheckoutExtrasGrid";
import { useSubmitDeviation } from "../hooks";

import { intl } from "./WeekEditorConfirmOrderExperimental.Intl";

export const WeekEditorAddMore = ({ week, year }: WeekYear) => {
  useIntercom(false);
  const router = useRouter();

  const { enabled: isWeekEditorRedesignEnabled, isReady } = useFlag(
    FEATURE_FLAGS.WEEK_EDITOR_REDESIGN,
  );

  const { handleSubmitDeviation, isSubmitDeviationLoading } =
    useSubmitDeviation({
      week,
      year,
    });

  const { data: extendedBasket } = useExtendedBasketData({
    week,
    year,
  });

  const { selectedProducts = [], chosenProducts } = extendedBasket;

  const isBelowMinMeals =
    selectedProducts.filter(isPickAndMixProduct).length <
    extendedBasket.minMeals;

  //TODO: can be removed after redesign is fully rolled out`
  //lines below preventing enter new created page directly
  useEffect(() => {
    if (isNotEmptyArray(chosenProducts) && !isWeekEditorRedesignEnabled) {
      router.replace(BRAND_PATHS.WEEK_EDITOR_CONFIRMATION_PAGE({ week, year }));
    }
  }, [chosenProducts, router, week, year, isWeekEditorRedesignEnabled]);

  if (
    !isReady ||
    isEmptyArray(chosenProducts) ||
    !isWeekEditorRedesignEnabled
  ) {
    return <PageLoader />;
  }

  return (
    <AppShell title={intl.LAST_CHANCE}>
      <AppShell.Meta />
      <AppShell.Sticky anchor="top" float>
        <AppShell.Header variant="my-menu" />
      </AppShell.Sticky>

      <AppShell.Main grid>
        <div className="col-span-full lg:col-span-5 xl:col-span-9">
          <CheckoutExtrasGrid week={week} year={year} />
        </div>

        <section className="relative flex flex-col gap-4 col-span-full lg:col-span-3">
          <Card>
            <Summary week={week} year={year} />

            <div className="fixed z-20 w-full px-4 pb-4 -translate-x-1/2 md:p-0 md:relative md:translate-x-0 bottom-18 md:bottom-0 left-1/2 md:left-0">
              <Button
                disabled={isBelowMinMeals}
                loading={isSubmitDeviationLoading}
                primary
                onClick={handleSubmitDeviation}
                full
              >
                {intl.CONFIRM_ORDER}
              </Button>
            </div>
          </Card>
        </section>
      </AppShell.Main>

      <AppShell.Sticky anchor="bottom">
        <AppShell.TabBar variant="my-menu" />
      </AppShell.Sticky>
    </AppShell>
  );
};
